import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useActivations from '../../Activations/components/hooks/useActivations';
import { V3_AREAS, areaTarget } from '../flags';
import { AREA_ICONS, IconActivations, IconClose } from './icons';

/*
 * Find (concept v12): Find replaces ⌘K on touch — a full-screen
 * takeover on mobile, the same surface as a kit popup on desktop
 * (CSS decides). It searches everything the rail reaches: places
 * (the areas, bridges included) and activations by name.
 */

interface ActivationLite {
    id: number;
    name: string;
    date?: string;
    isArchived?: boolean;
}

interface Result {
    key: string;
    label: string;
    sub?: string;
    icon: React.ComponentType;
    target: string;
}

function fmtDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
}

export default function Find({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate();
    const { getActivations } = useActivations();
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(0);
    const [activations, setActivations] = useState<ActivationLite[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        let alive = true;
        getActivations()
            .then((list: ActivationLite[]) => {
                if (alive && Array.isArray(list)) {
                    setActivations(list.filter((a) => !a.isArchived));
                }
            })
            .catch(() => {
                /* empty ≠ failed for places; activations just stay absent */
            });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const q = query.trim().toLowerCase();
    const { activationResults, placeResults } = useMemo(() => {
        const matchedActivations = (
            q
                ? activations.filter((a) =>
                      (a.name ?? '').toLowerCase().includes(q),
                  )
                : activations
        ).slice(0, q ? 8 : 3);
        const matchedPlaces = V3_AREAS.filter((a) =>
            q ? a.label.toLowerCase().includes(q) : true,
        );
        const activationResults: Result[] = matchedActivations.map((a) => ({
            key: `act-${a.id}`,
            label: a.name,
            sub: fmtDate(a.date),
            icon: IconActivations,
            target: `/activations/${a.id}`,
        }));
        const placeResults: Result[] = matchedPlaces.map((a) => ({
            key: `area-${a.id}`,
            label: a.label,
            icon: AREA_ICONS[a.id],
            target: areaTarget(a),
        }));
        return { activationResults, placeResults };
    }, [q, activations]);

    const flat = [...activationResults, ...placeResults];
    const clamped = Math.min(selected, Math.max(flat.length - 1, 0));

    const go = (r: Result | undefined) => {
        if (!r) return;
        onClose();
        navigate(r.target);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelected((v) => Math.min(v + 1, flat.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelected((v) => Math.max(v - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            go(flat[clamped]);
        }
    };

    const renderRows = (rows: Result[], offset: number) =>
        rows.map((r, i) => {
            const Icon = r.icon;
            const idx = offset + i;
            return (
                <button
                    key={r.key}
                    className={idx === clamped ? 'row active' : 'row'}
                    onMouseEnter={() => setSelected(idx)}
                    onClick={() => go(r)}
                >
                    {Icon && <Icon />}
                    {r.label}
                    {r.sub && <span className="frow-st">{r.sub}</span>}
                </button>
            );
        });

    return (
        <>
            <div className="scrim" onClick={onClose} />
            <div className="findlay" role="dialog" aria-label="Find">
                <div className="fhead">
                    <input
                        ref={inputRef}
                        value={query}
                        placeholder="Find…"
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setSelected(0);
                        }}
                        onKeyDown={onKeyDown}
                    />
                    <button
                        className="ic"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <IconClose />
                    </button>
                </div>
                <div className="fdiv" />
                <div className="nav">
                    {activationResults.length > 0 && (
                        <div className="flab">
                            {q ? 'Activations' : 'Recent'}
                        </div>
                    )}
                    {renderRows(activationResults, 0)}
                    {placeResults.length > 0 && (
                        <div className="flab">Places</div>
                    )}
                    {renderRows(placeResults, activationResults.length)}
                    {flat.length === 0 && (
                        <div className="fempty">
                            Nothing matches “{query}”.
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
